import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import "../../assets/css/globalPopup.scss"; // Use the SCSS file

/**
 * GlobalPopup Component
 * 
 * @param {boolean} isOpen - Whether the popup is open
 * @param {string} type - Type of popup ('info', 'success', 'warning', 'error')
 * @param {string} title - Title of the popup
 * @param {string|React.ReactNode} content - Content to display (can be HTML string or React element)
 * @param {Array} options - Array of option buttons
 * @param {Function} onClose - Callback when popup is closed
 * @param {boolean} showCloseButton - Whether to show the close button
 * @param {boolean} backdrop - Whether clicking backdrop closes popup
 */
const GlobalPopup = ({
  isOpen,
  type = 'info',
  title,
  content,
  options = [{ text: 'OK', action: null, type: 'primary' }],
  onClose,
  showCloseButton = true,
  backdrop = true
}) => {
  const dialogRef = useRef(null);

  /**
   * Check if content is an HTML string
   * @param {any} content - Content to check
   * @returns {boolean} - True if content appears to be HTML
   */
  const isHtmlString = (content) => {
    return typeof content === 'string' && 
           content.trim().length > 0 && 
           (content.includes('<') && content.includes('>'));
  };

  /**
   * Render content based on its type
   * @param {string|React.ReactNode} content - Content to render
   * @returns {React.ReactNode} - Rendered content
   */
  const renderContent = (content) => {
    if (!content) return null;
    
    if (isHtmlString(content)) {
      // Render HTML string safely with XSS sanitization
      return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />;
    } else if (typeof content === 'string') {
      // Render plain text in a paragraph
      return <p>{content}</p>;
    } else {
      // Render React elements as-is
      return <div>{content}</div>;
    }
  };

  // Handle dialog open/close when isOpen changes
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal(); // This puts the dialog in the top layer
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  // Handle dialog close events
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = () => {
      if (onClose) {
        onClose();
      }
    };

    const handleCancel = (e) => {
      e.preventDefault(); // Prevent default ESC behavior
      if (onClose) {
        onClose();
      }
    };

    dialog.addEventListener('close', handleClose);
    dialog.addEventListener('cancel', handleCancel);

    return () => {
      dialog.removeEventListener('close', handleClose);
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return 'fa-check-circle';
      case 'warning':
        return 'fa-exclamation-triangle';
      case 'error':
        return 'fa-times-circle';
      case 'info':
      default:
        return 'fa-info-circle';
    }
  };

  const handleOptionClick = (option) => {
    if (option.action && typeof option.action === 'function') {
      option.action();
    }
    if (onClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    // Close when clicking on the dialog backdrop (outside the popup content)
    if (e.target === dialogRef.current && backdrop && onClose) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="global-popup-dialog"
      onClick={handleBackdropClick}
    >
      <div className={`global-popup ${type}`}>
        {showCloseButton && (
          <button
            className="global-popup-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}

        <div className="global-popup-header">
          <div className="global-popup-icon">
            <i className={`fa ${getIcon()}`}></i>
          </div>
          {title && (
            <h3 className="global-popup-title">{title}</h3>
          )}
        </div>

        <div className="global-popup-content">
          {renderContent(content)}
        </div>

        <div className="global-popup-actions">
          {options.map((option, index) => (
            <button
              key={index}
              className={`global-popup-btn ${option.type || 'default'}`}
              onClick={() => handleOptionClick(option)}
              disabled={option.disabled}
            >
              {option.icon && <i className={`fa ${option.icon}`}></i>}
              {option.text}
            </button>
          ))}
        </div>
      </div>
    </dialog>
  );
};

export default GlobalPopup;
